import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { createCustomerCart } from "../utils/createCart"
import { getActiveCustomerCart } from "../utils/getCart"
import { mutationDeleteCustomerCart } from "./mutationDeleteCustomerCart"
import { mutationTransferOrderItemsToCart } from "./mutationTransferOrderItemsToCart"

const commerceApi = CommerceService.api()

export const mutationCopyPicklistToCart = {
  async copyPicklistToCart(parent, args, context, info) {
    let { customerId, customerNumber, orderNumber, loggedInBranch } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    if (!customerId || !orderNumber) {
      throw new Error("Missing data")
    }

    const customer = await getCustomer({ id: customerId, number: customerNumber })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    // Get existing cart or create new one
    const customerCarts = await getActiveCustomerCart(customer.id, user.employeeNumber)
    let customerCart = customerCarts[0]

    // Track if we deleted a cart (to skip the active cart check when creating new one)
    let deletedExistingCart = false

    // If an active cart exists, delete it and create a fresh one
    if (customerCart && customerCart.cartState === "Active") {
      await mutationDeleteCustomerCart.deleteCustomerCart(
        null,
        {
          customerId,
          customerNumber,
          cartId: customerCart.id,
        },
        context,
        null,
      )
      deletedExistingCart = true
    }

    // Create new cart (skip active cart check if we just deleted one to avoid race condition)
    customerCart = await createCustomerCart(customer, null, "EUR", null, null, user, {
      skipActiveCartCheck: deletedExistingCart,
    })

    if (!customerCart) {
      throw new Error("Could not create or access customer cart")
    }

    // Transfer order items to cart
    let updatedCart = await mutationTransferOrderItemsToCart.transferOrderItemsToCart(
      null,
      {
        customerId,
        customerNumber,
        orderNumber,
        loggedInBranch,
        withPrice: true,
      },
      context,
      null,
    )

    // Get order data with all additional fields
    const orderData = await getOrderData(orderNumber)

    if (!orderData) {
      throw new Error("Order data not found")
    }

    // Get updated cart version after item transfer
    const currentCart = await getActiveCustomerCart(customer.id, user.employeeNumber)

    const cart = currentCart[0]
    if (!cart) {
      throw new Error("Cart not found after item transfer")
    }

    // Prepare actions to copy address, shipping method, and additional fields
    const actions = []

    // Copy shipping address if available
    if (orderData.shippingStreet || orderData.shippingCity) {
      actions.push({
        action: "setShippingAddress",
        address: {
          firstName: orderData.shippingAddress1 || "",
          lastName: orderData.shippingAddress2 || "",
          streetName: orderData.shippingStreet || "",
          additionalStreetInfo: orderData.shippingAdditionalStreetInfo || "",
          city: orderData.shippingCity || "",
          postalCode: orderData.shippingPostalCode || "",
          country: orderData.shippingCountry || "DE",
        },
      })
    }

    // Copy shipping method - use deliveryMethod as shippingMethodName
    if (orderData.deliveryMethod) {
      const zeroTaxCategory = (await commerceApi.taxCategories().withKey({ key: "default-vat-de-0" }).get().execute())
        ?.body

      if (zeroTaxCategory) {
        actions.push({
          action: "setCustomShippingMethod",
          shippingMethodName: orderData.deliveryMethod, // Set deliveryMethod as shippingMethodName
          shippingRate: {
            price: {
              currencyCode: "EUR",
              centAmount: cart?.custom?.fields?.freightSurcharge?.centAmount || 0,
            },
          },
          taxCategory: { id: zeroTaxCategory.id, typeId: "tax-category" },
        })
      }
    }

    // Get custom type for cart
    const customTypeCart = (await commerceApi.types().withKey({ key: "customCartTypeKey" }).get().execute())?.body

    if (customTypeCart) {
      // Prepare custom fields - only include fields that have values
      const customFields = {
        createdByEmployeeNumber: cart?.custom?.fields?.createdByEmployeeNumber || user.employeeNumber,
        ...(cart.custom?.fields?.picklistNumber && { picklistNumber: cart.custom.fields.picklistNumber }),
      }
      // Copy additional fields from order if they have

      if (orderData.valueDate) {
        customFields.paymentTill = orderData.valueDate
      }
      if (orderData.shippingDate) {
        customFields.deliveryDate = orderData.shippingDate
      }
      if (orderData.customerOrderText) {
        customFields.orderText = orderData.customerOrderText
      }
      if (orderData.customerOrderNumber) {
        customFields.orderNumber = orderData.customerOrderNumber
      }
      if (orderData.infoText1) {
        customFields.additionalInformationOne = orderData.infoText1
      }
      if (orderData.infoText2) {
        customFields.additionalInformationTwo = orderData.infoText2
      }
      if (orderData.infoText3) {
        customFields.additionalInformationThree = orderData.infoText3
      }
      if (orderData.costUnit) {
        customFields.costUnit = orderData.costUnit
      }

      if (orderData.shippingCosts) {
        customFields.freightSurcharge = {
          centAmount: Math.round(orderData.shippingCosts * 100), // Convert from EUR to cent
          type: "centPrecision",
          currencyCode: "EUR",
          fractionDigits: 2,
        }
      }

      actions.push({
        action: "setCustomType",
        type: {
          typeId: "type",
          id: customTypeCart.id,
        },
        fields: customFields,
      })
    }

    // Apply all actions if any exist
    if (actions.length > 0) {
      const payload = {
        version: cart.version,
        actions,
        storeKey: cart?.store?.key,
      }

      updatedCart = (
        await commerceApi
          .carts()
          .withId({ ID: cart.id })
          .post({
            body: JSON.stringify(payload),
          })
          .execute()
      )?.body
    }
    return updatedCart
  },
}

/**
 * Gets order data with all fields needed for copying to cart
 *
 * @param {string} orderNumber
 * @returns {Promise<Object|null>}
 */
const getOrderData = async (orderNumber) => {
  const graphiQLQueryObject = [
    {
      method: "findOrder",
      variables: { orderNumber },
      fields: [
        "orderNumber",
        "orderNumber2",
        "customerNumber",
        "deliveryMethod",
        "deliveryMethodDescription",
        "shippingStreet",
        "shippingCity",
        "shippingPostalCode",
        "shippingCountry",
        "shippingAddress1",
        "shippingAddress2",
        "shippingCosts",
        "shippingAdditionalStreetInfo",
        "valueDate",
        "shippingDate",
        "customerOrderText",
        "customerOrderNumber",
        "infoText1",
        "infoText2",
        "infoText3",
        "costUnit",
      ],
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)

  let customerOrderResponse
  try {
    const response = await EinzaGraphiQLService.getData(mappedQuery)
    customerOrderResponse = response?.data?.findOrder
  } catch (error) {
    throw new Error("Order data not found", error)
  }

  return customerOrderResponse || null
}
